<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1200px] mx-auto">

    <!-- Header -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-4 min-w-0">
        <button type="button" (click)="backToList()"
          class="h-10 w-10 flex-shrink-0 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-amber-300 transition-colors"
          matTooltip="Back to inventory" aria-label="Back to inventory">
          <mat-icon>arrow_back</mat-icon>
        </button>
        @if (row && imageOf(row)) {
          <img [src]="imageOf(row)" [alt]="row.productName" class="h-12 w-12 rounded-xl object-cover ring-1 ring-black/5 shadow-sm flex-shrink-0" />
        }
        <div class="min-w-0">
          <div class="flex items-center gap-1.5 text-xs text-gray-400">
            <button type="button" class="hover:text-gray-700" (click)="backToList()">Inventory</button>
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">chevron_right</mat-icon>
            <span>{{ canEdit ? 'Edit' : 'View' }}</span>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 tracking-tight truncate max-w-[70vw] lg:max-w-[520px]">{{ loading ? 'Loading…' : row?.productName || 'Item' }}</h1>
          @if (row) {
            <div class="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-gray-500">
              @if (row.label) { <span class="font-semibold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800">{{ row.label }}</span> }
              <span class="font-mono px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700">{{ row.sku || 'No SKU' }}</span>
              @if (row.barcode) { <span class="flex items-center gap-1 font-mono"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">barcode</mat-icon>{{ row.barcode }}</span> }
              @if (row.category) { <span class="text-gray-400">{{ row.category }}</span> }
            </div>
          }
        </div>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
        @if (nav.length) {
          <app-record-nav noun="item" listName="Inventory" [position]="position" [total]="nav.length"
            [prevName]="navName(prev)" [nextName]="navName(next)" [disabled]="loading || saving" (prev)="goTo(prev)" (next)="goTo(next)"></app-record-nav>
        }
        @if (row?.productSlug) {
          <a class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:no-underline shadow-sm transition-colors"
            [href]="frontEndURL + row!.productSlug" target="_blank" rel="noopener"
            [matTooltip]="row!.productActive === false ? 'This product is inactive, so customers can\'t see it' : 'Open the product page on the website'">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-emerald-500">open_in_new</mat-icon> View on website
          </a>
        }
        @if (row) {
          <button type="button" class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-sky-300 hover:bg-sky-50 shadow-sm transition-colors" (click)="openHistory()">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-sky-600">history</mat-icon> Stock history
          </button>
        }
      </div>
    </div>

    @if (loading) {
      <div class="rounded-2xl bg-white border border-gray-100 py-24 flex flex-col items-center justify-center text-sm text-gray-500">
        <mat-spinner diameter="40"></mat-spinner><span class="mt-3">Loading item&hellip;</span>
      </div>
    } @else if (notFound || !row) {
      <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">search_off</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">That item isn't in the inventory</p>
        <button type="button" class="mt-4 text-xs font-semibold text-amber-700 hover:underline" (click)="backToList()">Back to inventory</button>
      </div>
    } @else {

    @if (!canEdit) {
      <div class="mb-5 flex items-center gap-2 px-4 py-3 rounded-2xl bg-sky-50 border border-sky-100 text-sm text-sky-800">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">visibility</mat-icon> You can look at this item, but not change it.
      </div>
    }

    <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">

      <!-- ============ FORM ============ -->
      <div class="flex flex-col gap-5">

        <!-- Stock -->
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center justify-between gap-3 mb-5">
            <div class="flex items-center gap-3">
              <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><mat-icon class="!text-[19px] !h-[19px] !w-[19px]">warehouse</mat-icon></span>
              <div><h2 class="text-[15px] font-bold text-gray-900">Stock</h2><p class="text-xs text-gray-500">How many are in each yard</p></div>
            </div>
            <div class="text-right">
              <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Total</div>
              <div class="text-2xl font-bold text-gray-900 tabular-nums leading-none">{{ madeToOrder ? '–' : (total | number) }}</div>
            </div>
          </div>

          @if (madeToOrder) {
            <div class="mb-4 flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-xl px-3.5 py-2.5">
              <mat-icon class="!text-[16px] !h-4 !w-4 flex-shrink-0">info</mat-icon>
              This item has a very large quantity, which the site treats as "made to order". Only change it if that is no longer true.
            </div>
          }

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            @for (yard of [{ key: 'kettering', label: 'Kettering', was: row.kStockQty }, { key: 'markstey', label: 'Markstey', was: row.mStockQty }]; track yard.key) {
            <div class="rounded-2xl border p-4 transition-colors" [ngClass]="(yard.key === 'kettering' ? kettering : markstey) != yard.was ? 'border-amber-300 bg-amber-50/50' : 'border-gray-100 bg-gray-50/60'">
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{{ yard.label }}</span>
                @if ((yard.key === 'kettering' ? kettering : markstey) != yard.was) {
                  <span class="text-[11px] font-semibold text-amber-700 tabular-nums">was {{ yard.was | number }}</span>
                }
              </div>
              <div class="mt-2 flex items-center gap-2">
                <button type="button" class="h-11 w-11 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50 flex items-center justify-center flex-shrink-0 disabled:opacity-40" [disabled]="!canEdit" (click)="adjust($any(yard.key), -1)" aria-label="One less"><mat-icon>remove</mat-icon></button>
                @if (yard.key === 'kettering') {
                  <input type="number" min="0" step="1" name="kettering" [disabled]="!canEdit" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-center text-xl font-bold tabular-nums" [(ngModel)]="kettering" />
                } @else {
                  <input type="number" min="0" step="1" name="markstey" [disabled]="!canEdit" class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-center text-xl font-bold tabular-nums" [(ngModel)]="markstey" />
                }
                <button type="button" class="h-11 w-11 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:bg-amber-50 flex items-center justify-center flex-shrink-0 disabled:opacity-40" [disabled]="!canEdit" (click)="adjust($any(yard.key), 1)" aria-label="One more"><mat-icon>add</mat-icon></button>
              </div>
              @if (canEdit) {
                <div class="mt-2 flex justify-center gap-1">
                  @for (step of [-10, -5, 5, 10]; track step) {
                    <button type="button" class="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-[11px] font-semibold text-gray-500 hover:border-amber-300 hover:text-amber-700 tabular-nums" (click)="adjust($any(yard.key), step)">{{ step > 0 ? '+' + step : step }}</button>
                  }
                </div>
              }
            </div>
            }
          </div>
          @if (errors.stock) { <div class="mt-2 text-xs text-red-500">{{ errors.stock }}</div> }

          @if (stockChanged) {
            <div class="mt-5 pt-5 border-t border-gray-100">
              <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Why is the stock changing? *</label>
              <div class="mt-2 flex flex-wrap gap-2">
                @for (r of reasons; track r) {
                  <button type="button" class="px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all"
                    [ngClass]="reason === r ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'" (click)="reason = r">{{ r }}</button>
                }
              </div>
              @if (reason === 'Other') {
                <input class="mt-2 w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm" name="otherReason" maxlength="150" placeholder="A few words on what happened" [(ngModel)]="otherReason" />
              }
              <p class="mt-1.5 text-xs" [ngClass]="tried && reasonMissing ? 'text-red-500' : 'text-gray-400'">Kept in the item's stock history.</p>
            </div>
          }
        </section>

        <!-- Prices -->
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center gap-3 mb-5">
            <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><mat-icon class="!text-[19px] !h-[19px] !w-[19px]">payments</mat-icon></span>
            <div><h2 class="text-[15px] font-bold text-gray-900">Prices</h2><p class="text-xs text-gray-500">What customers pay{{ canSeeCost ? ', and what we pay' : '' }}</p></div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <div>
              <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Regular price *</label>
              <div class="relative mt-1.5"><span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                <input type="number" min="0" step="0.01" name="regularPrice" [disabled]="!canEdit" class="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm font-semibold" [(ngModel)]="regularPrice" /></div>
              @if (errors.price) { <div class="mt-1 text-xs text-red-500">{{ errors.price }}</div> }
            </div>
            @if (canSeeCost) {
              <div>
                <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" matTooltip="What we pay the supplier">Cost price</label>
                <div class="relative mt-1.5"><span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                  <input type="number" min="0" step="0.01" name="costPrice" placeholder="Not recorded" [disabled]="!canEdit" class="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm" [ngModel]="costPrice" (ngModelChange)="costPrice = $event === '' || $event === null ? null : $event" /></div>
                @if (errors.cost) { <div class="mt-1 text-xs text-red-500">{{ errors.cost }}</div> }
              </div>
            }
          </div>

          <label class="mt-5 flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all" [ngClass]="onSale ? 'border-rose-300 bg-rose-50/60' : 'border-gray-200 hover:border-gray-300'">
            <input type="checkbox" name="onSale" class="mt-0.5 h-4 w-4 accent-rose-500" [disabled]="!canEdit" [(ngModel)]="onSale" />
            <span><span class="block text-sm font-semibold text-gray-900">On sale</span><span class="block text-xs text-gray-500 mt-0.5">Customers pay a lower price, with the regular price shown struck through</span></span>
          </label>
          @if (onSale) {
            <div class="mt-3 max-w-xs">
              <label class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Sale price *</label>
              <div class="relative mt-1.5"><span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                <input type="number" min="0" step="0.01" name="discountedPrice" [disabled]="!canEdit" class="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm text-rose-600 font-semibold" [(ngModel)]="discountedPrice" /></div>
              @if (errors.discount) { <div class="mt-1 text-xs text-red-500">{{ errors.discount }}</div> }
              @else if (discountPercent !== null) { <div class="mt-1 text-xs text-gray-500">{{ discountPercent }}% off the regular price</div> }
            </div>
          }
        </section>

        <!-- Extreme Products -->
        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div class="flex items-center gap-3 mb-4">
            <span class="h-9 w-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><mat-icon class="!text-[19px] !h-[19px] !w-[19px]">link</mat-icon></span>
            <div><h2 class="text-[15px] font-bold text-gray-900">Extreme Products</h2><p class="text-xs text-gray-500">Another option of this product it's linked to, e.g. the single slab for a crate</p></div>
          </div>
          @if (variantOptions.length) {
            <select name="extremeProductVariantId" [disabled]="!canEdit"
              class="w-full px-3 py-2.5 rounded-xl border bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
              [ngClass]="extremeChanged ? 'border-amber-300' : 'border-gray-200'"
              [(ngModel)]="extremeProductVariantId">
              <option [ngValue]="null">None</option>
              @for (o of variantOptions; track o.productVariantId) {
                <option [ngValue]="o.productVariantId">{{ optionName(o) }}</option>
              }
            </select>
          } @else {
            <p class="text-sm text-gray-400">This product has no other options to link to.</p>
          }
        </section>
      </div>

      <!-- ============ SUMMARY ============ -->
      <div class="flex flex-col gap-5 lg:sticky lg:top-4">
        <section class="relative overflow-hidden rounded-2xl bg-neutral-900 text-white p-5 shadow-sm">
          <div class="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-amber-400/20 blur-2xl"></div>
          <div class="relative">
            <div class="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">At a glance</div>
            <div class="mt-3 flex items-end justify-between">
              <div>
                <div class="text-[10px] uppercase tracking-wide text-white/50">Customers pay</div>
                <div class="text-3xl font-bold tabular-nums">£{{ sellingPrice | number: '1.2-2' }}</div>
                @if (onSale && discountedPrice) { <div class="text-xs text-white/40 line-through tabular-nums">£{{ regularPrice | number: '1.2-2' }}</div> }
              </div>
              <span class="text-xs font-semibold px-2.5 py-1 rounded-lg"
                [ngClass]="stockStatus === 'OUT' ? 'bg-rose-500/20 text-rose-200' : stockStatus === 'LOW' ? 'bg-amber-400/20 text-amber-200' : stockStatus === 'MTO' ? 'bg-sky-400/20 text-sky-200' : 'bg-emerald-400/20 text-emerald-200'">
                {{ stockStatus === 'OUT' ? 'Out of stock' : stockStatus === 'LOW' ? 'Low stock' : stockStatus === 'MTO' ? 'Made to order' : 'In stock' }}
              </span>
            </div>
            <div class="mt-4 grid grid-cols-2 gap-2">
              <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Kettering</div><div class="text-lg font-bold tabular-nums">{{ madeToOrder ? '–' : (kettering | number) }}</div></div>
              <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Markstey</div><div class="text-lg font-bold tabular-nums">{{ madeToOrder ? '–' : (markstey | number) }}</div></div>
              @if (margin !== null) {
                <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Margin</div>
                  <div class="text-lg font-bold tabular-nums" [ngClass]="margin >= 30 ? 'text-emerald-300' : margin >= 15 ? 'text-amber-300' : 'text-rose-300'">{{ margin }}%</div></div>
              }
              @if (!madeToOrder && sellingPrice) {
                <div class="rounded-xl bg-white/[0.07] px-3 py-2" matTooltip="Units on hand × the price customers pay"><div class="text-[10px] uppercase tracking-wide text-white/50">Stock value</div><div class="text-lg font-bold tabular-nums">£{{ total * sellingPrice | number: '1.0-0' }}</div></div>
              }
            </div>
          </div>
        </section>

        <section class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-sm">
          <div class="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">Details</div>
          <div class="flex items-center justify-between py-1.5 border-b border-gray-50"><span class="text-gray-400">Coverage</span><span class="text-gray-700">{{ row.approxCoverage ? (row.approxCoverage | number: '1.0-4') + ' m²' : '–' }}</span></div>
          <div class="flex items-center justify-between py-1.5"><span class="text-gray-400">Price per m²</span><span class="text-gray-700">{{ row.priceMS ? '£' + (row.priceMS | number: '1.2-2') : '–' }}</span></div>
        </section>
      </div>
    </div>

    <!-- Save bar -->
    @if (canEdit) {
    <div class="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 bg-white/95 backdrop-blur rounded-2xl border border-gray-200 shadow-lg px-5 py-3">
      <div class="min-w-0 text-sm">
        @if (!changed) {
          <span class="text-gray-400">No changes yet</span>
        } @else if (blocker) {
          <span class="flex items-center gap-1.5" [ngClass]="tried ? 'text-red-600' : 'text-amber-700'"><mat-icon class="!text-[16px] !h-4 !w-4 flex-shrink-0">error_outline</mat-icon><span class="truncate">{{ blocker }}</span></span>
        } @else {
          <span class="flex items-center gap-1.5 text-emerald-600"><mat-icon class="!text-[16px] !h-4 !w-4">check_circle</mat-icon>Ready to save</span>
        }
      </div>
      <div class="flex items-center gap-2 ml-auto">
        @if (changed) {
          <button type="button" class="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors" (click)="discard()">Discard</button>
        } @else {
          <button type="button" class="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors" (click)="backToList()">Back to list</button>
        }
        @if (next) {
          <button type="button" [disabled]="!changed || saving" (click)="save(true)" matTooltip="Save, then open the next item"
            class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-all disabled:opacity-40 disabled:pointer-events-none">
            Save &amp; next <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">chevron_right</mat-icon>
          </button>
        }
        <button type="button" [disabled]="!changed || saving" (click)="save()"
          class="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-sm font-bold shadow-sm hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-40 disabled:pointer-events-none">
          @if (saving) { <span class="inline-block h-4 w-4 rounded-full border-2 border-neutral-900 border-t-transparent animate-spin"></span> }
          @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">save</mat-icon> }
          Save changes
        </button>
      </div>
    </div>
    }
    }
  </div>
  </div>
</app-main-layout>
